import {forwardRef, useState} from 'react';
import {scroller} from 'react-scroll';
import {Button, Icon, Link} from '@ezcater/tapas';
import {faChevronDown, faChevronUp} from '@fortawesome/pro-solid-svg-icons';
import useTranslation from 'next-translate/useTranslation';

import {
  SocialLinks,
  TrackedLinks,
  TranscendButton,
} from '@/components/global/layout/MarketplaceFooter';
import {EzcaterLogo} from '@/components/ui';
import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import useMediaQuery from '@/hooks/useMediaQuery';
import {
  ABOUT_US_PATH,
  ACCESSIBILITY_PATH,
  BLOG_PATH,
  BRAND_LIST_PATH,
  CA_NOTICE_OF_COLLECTION_PATH,
  CAREERS_PATH,
  CATERER_PARTNERS_PATH,
  CATERERS_NEAR_ME_PATH,
  CONTACT_PATH,
  CORPORATE_SOLUTIONS_PATH,
  CUSTOMER_HELP_CENTER_URL,
  FOOD_TYPE_HUB_PATH,
  PHYSICAL_MAIL_OPT_OUT_URL,
  PRIVACY_PATH,
  RECURRING_EMPLOYEE_MEALS_PATH,
  REPORTS_PATH,
  SECURITY_URL,
  TERMS_PATH,
} from '@/paths';

const COMPANY_NAV_LINKS = [
  {key: 'aboutUs', path: ABOUT_US_PATH},
  {key: 'careers', path: CAREERS_PATH},
  {key: 'blog', path: BLOG_PATH},
  {key: 'dataInsights', path: REPORTS_PATH},
];

const INFO_NAV_LINKS = [
  {key: 'contactUs', path: CONTACT_PATH},
  {key: 'faq', path: CUSTOMER_HELP_CENTER_URL},
  {key: 'security', path: SECURITY_URL},
];

const GET_STARTED_NAV_LINKS = [
  {key: 'mealProgram', path: RECURRING_EMPLOYEE_MEALS_PATH},
  {key: 'enterpriseSolutions', path: CORPORATE_SOLUTIONS_PATH},
  {key: 'restaurantPartner', path: CATERER_PARTNERS_PATH},
  {key: 'seeAllFoodTypes', path: FOOD_TYPE_HUB_PATH},
  {key: 'seeAllRestaurants', path: BRAND_LIST_PATH},
  {key: 'seeRestaurantsNearMe', path: CATERERS_NEAR_ME_PATH},
];

const GET_STARTED_SCROLL_LINKS = [
  {
    label: 'Start an order',
    to: 'start-new-order-event-address',
    offset: -300,
  },
];

const buildColophonLinks = (isHelpCenterTreatment: boolean) => [
  {key: 'privacy', path: PRIVACY_PATH},
  {key: 'terms', path: TERMS_PATH},
  {key: 'caNoticeAtCollection', path: CA_NOTICE_OF_COLLECTION_PATH},
  {key: 'accessibility', path: ACCESSIBILITY_PATH},
  ...(isHelpCenterTreatment
    ? [{key: 'optOutOfPhysicalMailing', path: PHYSICAL_MAIL_OPT_OUT_URL}]
    : []),
];

type HomeFooterNavLink = {
  key: string;
  path: string;
};

type HomeFooterScrollLink = {
  label: string;
  offset?: number;
  to: string;
  toMobile?: string;
};

type HomeFooterContentProps = {
  header: string;
  navLinks?: HomeFooterNavLink[];
  scrollLinks?: HomeFooterScrollLink[];
};

const HomeFooterContent: React.FC<HomeFooterContentProps> = ({header, navLinks, scrollLinks}) => {
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => {
    setIsExpanded(prevState => !prevState);
  };

  return (
    <div className="w-full border-t border-green-300 py-2 first:border-none lg:block lg:w-auto lg:border-none lg:p-0">
      <h3 className="hidden text-sm font-bold text-white lg:block">{header}</h3>
      <Button
        variant="secondary"
        className="flex w-full items-center justify-between px-0 text-white no-underline hover:bg-transparent active:bg-transparent lg:hidden"
        aria-label="expand"
        onClick={toggleExpand}
      >
        <h3 className="text-base font-bold">{header}</h3>
        <Icon icon={isExpanded ? faChevronUp : faChevronDown} />
      </Button>
      <ul className={isMobile && !isExpanded ? 'hidden' : ''}>
        {scrollLinks?.map(({label, offset, to}) => (
          <li key={to}>
            <Link
              className="text-base leading-8 text-white transition duration-250 ease-out hover:text-white"
              href={`#${to}`}
              onClick={event => {
                event.preventDefault();
                scroller.scrollTo(to, {smooth: true, offset});
              }}
            >
              {label}
            </Link>
          </li>
        ))}
        {navLinks && (
          <TrackedLinks
            className="text-base leading-8 text-white transition duration-250 ease-out hover:text-white"
            links={navLinks}
          />
        )}
      </ul>
    </div>
  );
};

const HomeFooter = forwardRef<HTMLElement, unknown>(function HomeFooter(_props, ref) {
  const {t} = useTranslation('home');
  const {variation} = useStringAssignmentExperimentDetails(EppoFlags.IntercomHelpCenter);
  const isHelpCenterTreatment = variation === 'treatment';

  return (
    <footer ref={ref} className="bg-green-700 p-4 text-white sm:px-8 lg:py-8">
      <div className="mx-auto max-w-screen-xl">
        <div className="flex flex-col border-b pb-6 lg:flex-row lg:justify-between lg:pt-6">
          <HomeFooterContent header="Company" navLinks={COMPANY_NAV_LINKS} />
          <HomeFooterContent header="Info" navLinks={INFO_NAV_LINKS} />
          <HomeFooterContent
            header="Get Started"
            navLinks={GET_STARTED_NAV_LINKS}
            scrollLinks={GET_STARTED_SCROLL_LINKS}
          />
          <div className="items-left flex flex-col pt-6 lg:order-first lg:pt-0">
            <EzcaterLogo fillClassName="fill-green-300" width={205} height={30} />
            <p className="pt-4 font-bold lg:font-normal">{t('footer.tag')}</p>

            <Button
              render={
                <a href="https://ezcater.onelink.me/T4QA/mys390y5">
                  {t('footer.appDownloadButton')}
                </a>
              }
              className="mt-4 max-w-48 bg-green-300 text-green-700 hover:bg-green-400"
            />

            <SocialLinks className="mt-4 flex items-center gap-4 text-2xl [&_a:hover]:text-gray-140 [&_a]:text-white" />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 py-6 text-sm text-white md:flex md:flex-row lg:py-2">
          <div>&copy; {new Date().getFullYear()} ezCater, Inc.</div>
          <TrackedLinks
            as="span"
            className="text-peppercorn-100 underline hover:text-white"
            links={buildColophonLinks(isHelpCenterTreatment)}
          />
          <TranscendButton className="text-peppercorn-100 underline hover:text-white" />
        </div>
      </div>
    </footer>
  );
});

export default HomeFooter;
